import React, { useCallback, useMemo } from 'react';
import { type RenderFn } from '@atlassian/forge-ui-types';
import type { MentionProvider } from '@atlaskit/mention/types';

import { type ComponentMap } from '..';
import { makeAvatar, makeNativeUserPicker } from '../../components';
import { makeADFRenderer } from '../../components/UIKit/adfrenderer';
import { UserGroup } from '../../components/UIKit/usergroup';
import { type BridgeConfig } from '../../custom-ui/bridge/types';
import { type ProductKey } from '../../components/UIKit1/userPicker';
import { uncurlyUuid } from './utils';

interface UseUIKitComponentsOptions {
	accountId: string | undefined;
	bridge?: BridgeConfig;
	cloudId?: string;
	components?: ComponentMap;
	mentionProvider?: Promise<MentionProvider>;
	product: ProductKey | 'sandbox';
	workspaceId?: string;
}

export const useUIKitComponents = ({
	accountId,
	bridge,
	cloudId,
	workspaceId,
	mentionProvider,
	product,
	components,
}: UseUIKitComponentsOptions): ((defaults: ComponentMap) => {
	AdfRenderer: (props: Parameters<RenderFn>[0]) => React.JSX.Element;
	Avatar: RenderFn;
	AvatarStack: (props: Parameters<RenderFn>[0]) => React.JSX.Element;
	UserPicker: ({ forgeDoc: { props } }: Parameters<RenderFn>[0]) => React.JSX.Element;
}) => {
	const injectedComponentsWithKey = useMemo(() => {
		return components
			? Object.keys(components).reduce<ComponentMap>((acc, key) => {
					const Component = components[key];
					return {
						...acc,
						// A key is required to ensure React creates new component instances when forgeDoc.key changes.
						// Without this key, React reuses the same component instance across different forgeDoc updates,
						// causing hook call order violations when the component structure changes between renders.
						[key]: ((args) => <Component {...args} key={args.forgeDoc.key} />) as RenderFn,
					};
				}, {})
			: {};
	}, [components]);

	const wiredComponents = useMemo(
		() => ({
			Avatar: makeAvatar(),
			AvatarStack: UserGroup,
			UserPicker: makeNativeUserPicker({
				mentionProvider,
				accountId,
				...(workspaceId ? { cloudId: 'bitbucket' } : { cloudId }),
				productAttributes: workspaceId ? { workspaceIds: [uncurlyUuid(workspaceId)] } : undefined,
				productKey: product,
			}),
			...{
				AdfRenderer: makeADFRenderer({
					bridge,
					onNavigate: bridge?.onNavigate,
				}),
			},
		}),
		[accountId, cloudId, mentionProvider, product, workspaceId, bridge],
	);

	const getComponents = useCallback(
		(defaults: ComponentMap) => ({
			...defaults,
			...wiredComponents,
			...injectedComponentsWithKey,
		}),
		[injectedComponentsWithKey, wiredComponents],
	);

	return getComponents;
};
